export const documentsArticles = [
    {
        id: 'documents.overview',
        title: 'Documents Overview',
        category: 'Documents',
        order: 1,
        summary: 'Managing project documents and files',
        content: `
The Documents page provides a central repository for all your project files and documentation.

**Supported File Types:**

- PDF documents
- Images (JPG, PNG, etc.)
- Office documents (Word, Excel)
- CAD drawings (DWG, DXF)
- BIM models (IFC)

**Key Features:**

- Upload and organize files
- Preview documents in-browser
- Track version history
- Add comments and markup
- Define custom attributes
- Manage document workflows
    `,
        expandableSections: [
            {
                title: 'Document Grid',
                content: `
The grid shows all documents with:

- **Name** - File name
- **Type** - File type/extension
- **Size** - File size
- **Job** - Associated job
- **Uploaded** - Upload date
- **By** - Who uploaded it

Use folders to organize documents, or filter by job/type.
        `,
            },
            {
                title: 'Folder Structure',
                content: `
Organize documents into folders:

- Create folders for each job
- Use subfolders for categories (Drawings, Specs, Correspondence)
- Drag files between folders
- Share folder structures across jobs
- Let Claude build the tree for a new job in one step (see "Folders built by Claude" below), or apply a saved folder template
        `,
            },
            {
                title: 'Folders built by Claude (new job set-up)',
                content: `
For a new job you can upload the files first and let Claude create the folder
structure and file them, instead of making folders one by one.

1. Create the job and upload its documents against it (Documents → Upload, choose
   the job; or drop them into the job on Costed Drive). No folder is needed yet.
2. Ask Costed, or Claude through the connector: *"Set up the folders for J2602 like
   J2601, then file my uploads into the right folders."* You can also describe the
   structure yourself: *"Create 01 - Drawings with Architectural and Structural
   under it, 02 - Contract, 03 - Consultants…"*.
3. Claude shows the full list of paths on an approval card before anything is
   created. Approve it, then approve the filing cards that follow.
4. Documents → the job now shows the tree in the folder rail, each upload sits in its
   folder, and AI Activity lists every folder and filing with an **Open** link.

**Rules the tool follows**

- Folders belong to the job (they appear under the job filter here and under
  **Jobs → the job** on Costed Drive), unless you ask for workspace-wide folders.
- A folder that already exists with that name in that place is reused, never
  duplicated — asking twice is harmless. Names are compared ignoring case.
- Up to 100 paths and 8 levels in one request; characters Windows cannot use in a
  name are replaced with a dash.
- Creating folders needs a read-write key or connector; a read-only one can only list.
- Claude never deletes or moves folders. Removing or renaming stays in the folder
  rail's right-click menu.

For developers the same call is \`POST /api/documents/collections/ensure\` with
\`{ jobId, paths: ["01 - Drawings", "01 - Drawings/Architectural"] }\`.
        `,
            },
            {
                title: 'A document can live in several folders',
                content: `
Filing is by **link**, not by location — the same file can sit in more than one
folder at once. A supplier invoice can be in its job folder *and* in
**Company Admin / Accounts - Invoices & Bills**, and it is the same document in
both: rename it or upload a new version once and every folder shows the change.

- **Add to Folder…** (right-click) files documents somewhere new. Search the
  folder list by path — "admin invoice" finds *Company Admin / Accounts -
  Invoices & Bills* — and tick several folders to file into all of them at once.
  Documents never leave the folders they are already in.
- **Remove from "<folder>"** (right-click, shown when you have a folder open)
  takes the document out of *that* folder only. It is **not** a delete — the
  file stays in its other folders and in All Documents.
- **Delete** sends the document to the Recycle Bin, so it leaves *every* folder.
  Restoring it from the Recycle Bin puts it back in all of them.

The preview window lists every folder a document is filed in, under
**Details → Linked Entities → Folders**.
        `,
            },
            {
                title: 'Linking to a document from anywhere (permanent links)',
                content: `
Every document has a **permanent address** that opens it inside Costed:

\`https://app.costed.app/documents?doc=<document id>\`

Put it on a drawing sheet in your takeoff tool, in a spreadsheet, in a
programme, or in an email to a colleague. Opening it:

- asks for a Costed login if you are not signed in, then lands on the same page;
- opens the Documents page with the file **previewed and selected**, so version
  history, folders, tags, Linked To, activity and download are all there — the
  same as clicking the eye icon in the grid;
- shows the **latest version** even if the link was made before a new version
  was uploaded. Add \`&v=3\` to pin version 3 instead;
- works however many folders the file is in (the folder is not part of the link);
- never expires. A file that has been deleted, or that you cannot see, gives a
  clear message rather than a blank page.

Folders have the same kind of link, \`/documents?folder=<folder id>\`, and a
job's documents open with \`/documents?job=<job id>\`.

**Setting up a new job's folders with Claude:** Ask Costed and the MCP connector can
create a whole folder tree in one step with **costed_create_document_folders** — give it
the job number and the paths ("01 - Drawings/Architectural", …), or ask it to copy the
structure of a job you like. Folders that already exist are reused, so asking twice
creates nothing. It then files the documents you uploaded with **costed_file_document**,
and every folder and filing is listed under AI Activity with an Open link.

Ask Costed and the MCP connector return the link as \`url\` on every document
from **costed_list_documents** and **costed_search_documents**, on every folder
from **costed_list_document_folders**, and as \`documentsUrl\` on
**costed_get_job** — so an agent placing hyperlinks never has to build one.
Developers get the same \`url\` field from \`GET /api/documents\`,
\`/api/documents/search\`, \`/api/documents/:id\` and \`/api/documents/collections\`.
        `,
            },
            {
                title: 'Costed Drive: your documents as a network drive',
                content: `
Costed Drive lets Windows Explorer, macOS Finder, Linux file managers and the
iPhone Files app open your Documents library as a network drive, with no extra
software. Open files in place, drop new ones in, save and they land in Costed.

**Address:** \`https://app.costed.app/dav\`
**Sign in:** your Costed email as the user name and an **API key** as the
password (Settings → API keys; owners and admins create keys). A **read-only**
key browses and opens; a **read-write** key can also save, rename, move and
delete. An **admin** key can delete folders.

**Saving from the drive**

- Save a new file into a job folder and it becomes a document in that folder,
  on that job, with the key named as the uploader on its activity trail.
- Save over a file that already exists there (same name, same folder) and
  Costed records a **new version** — the file keeps its folders, pin and
  history. Word, Excel and Finder save through a temporary file; that becomes
  the new version too, and the temporary file is not kept.
- New folder in Explorer or Finder creates a Costed folder in the same place.
- Rename a file or folder and Costed renames it; drag a file to another folder
  of the same job and it is re-filed there. Copy a file to another folder and
  the same document is filed in both, as "Add to Folder" does.
- Delete a file and it goes to Costed's **recycle bin** (restore it there). A
  read-write key can delete the files it created; an admin key can delete any
  file, and empty folders. Files in \`_All files\` cannot be changed from the
  drive; \`_Unfiled\` accepts new files.
- Files over 100 MB, and moves between jobs or between a job and Company, are
  done in Costed rather than on the drive.

**What you see**

- **Jobs / <job number> - <job name> /** the job's folders — its own, and any
  company-wide folder tree that holds the job's documents (for example a
  numbered filing tree, or Company Admin / Accounts) showing **only that job's
  files** — plus **_All files** (every document on the job, wherever it is
  filed) and **_Unfiled** (documents on the job that are in no folder)
- **Company /** every workspace-wide folder with everything in it, whatever
  job the files belong to

A document filed in two folders appears in both. Deleted documents and older
versions are not shown. Names that would be illegal on Windows are shown with
those characters replaced by a dash.

**Connecting**

- **Windows:** This PC → Map network drive → Folder \`https://app.costed.app/dav\`,
  tick "Connect using different credentials", enter your email and the key.
  Word, Excel and PowerPoint block *opening* files from a password-signed drive
  ("sign-in method that may be unsecure"); the Windows section of Settings →
  API & Webhooks → Connect as a drive has the one-line fix (and the one for
  files over 50 MB). Changed the key? Remove the app.costed.app entry in
  Credential Manager and map the drive again, or Windows keeps sending the old one
- **macOS:** Finder → Go → Connect to Server → \`https://app.costed.app/dav\`
- **iPhone / iPad:** Files → ⋯ → Connect to Server
- **Linux:** \`davs://app.costed.app/dav\` in GNOME Files or Dolphin

Revoking the key in Settings disconnects the drive at the next request.
        `,
            },
            {
                title: 'Handing a file to another tool (download links)',
                content: `
For a link a *person* opens, use the permanent link above. The links here are
different: sometimes another tool needs the file itself — a takeoff program, an extraction
service, an email you are writing somewhere else, or an AI connected through the
MCP server. Costed can issue a **signed download link** for any document:

- The link fetches that one file with **no login**, and stops working when it
  expires — **24 hours** by default, 7 days at most. Anyone holding the link
  can download the file until then, so share it only with the task that needs it.
- Ask Costed and the MCP connector issue links with **costed_document_links**
  (up to 50 documents at a time, with an expiry you choose). Developers use
  \`POST /api/documents/:id/link\` or \`POST /api/documents/links\`.
- Every link issued is recorded on the document's **activity trail** ("link",
  with who asked and when it expires) and, for the assistant, in **AI Activity**.
- A link is a download by default; add \`?inline=1\` to view a PDF or image in the
  browser instead.
- Rotating the workspace's server secret invalidates every outstanding link.
        `,
            },
        ],
        keywords: ['documents', 'files', 'attachments', 'drawings', 'PDFs', 'download link', 'share link', 'permanent link', 'deep link', 'hyperlink', 'url', 'external', 'MCP', 'zzTakeoff', 'drive', 'webdav', 'network drive', 'explorer', 'finder', 'map drive', 'folders', 'folder structure', 'new job folders', 'create folders', 'claude folders'],
        route: '/documents',
        relatedArticles: ['documents.upload', 'documents.preview', 'documents.versions'],
    },
    {
        id: 'documents.upload',
        title: 'Uploading Documents',
        category: 'Documents',
        order: 2,
        summary: 'How to upload files to the document library',
        content: `
Upload documents to store them centrally and make them available to your team.

**Upload Methods:**

- Drag and drop files
- Click to browse and select
- Bulk upload multiple files
    `,
        expandableSections: [
            {
                title: 'Uploading Files',
                content: `
**Drag and Drop:**
1. Drag files from your computer
2. Drop onto the document area
3. Files upload automatically

**Browse and Select:**
1. Click **Upload** button
2. Select files in the dialog
3. Click **Open**

**Bulk Upload:**
- Select multiple files at once
- Or drag a folder (where supported)
- All files upload in parallel
        `,
            },
            {
                title: 'Upload Settings',
                content: `
When uploading, you can set:

- **Folder** - Where to store the file
- **Job** - Associated job
- **Attributes** - Custom metadata
- **Description** - Notes about the file

These can be set before or after upload.
        `,
            },
            {
                title: 'File Size Limits',
                content: `
Maximum file sizes:

- Individual files: 100 MB
- Batch upload: 500 MB total

For larger files, contact your administrator.
        `,
            },
        ],
        keywords: ['upload', 'add files', 'attach', 'import files'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.preview'],
    },
    {
        id: 'documents.preview',
        title: 'Document Preview',
        category: 'Documents',
        order: 3,
        summary: 'Previewing documents in the browser',
        content: `
Preview documents directly in the browser without downloading.

**Supported Previews:**

- PDF - Full preview with zoom and navigation
- Images - Image viewer with zoom
- Office - View Word/Excel/PowerPoint
- CAD - 2D drawing viewer
- BIM - 3D model viewer
    `,
        expandableSections: [
            {
                title: 'Using the Preview',
                content: `
1. Double-click a document
2. Or right-click > Preview
3. The preview opens in a panel

**Preview Controls:**
- Zoom in/out
- Navigate pages
- Download original
- Print
- Full screen mode
        `,
            },
            {
                title: 'CAD and BIM Viewing',
                content: `
**CAD Files (DWG/DXF):**
- 2D view with pan/zoom
- Layer visibility control
- Measurement tools

**BIM Files (IFC):**
- 3D model rotation
- Section views
- Element selection
- Property inspection
        `,
            },
        ],
        keywords: ['preview', 'view', 'viewer', 'open', 'display'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.upload'],
    },
    {
        id: 'documents.versions',
        title: 'Version History',
        category: 'Documents',
        order: 4,
        summary: 'Managing document versions and revisions',
        content: `
Track changes to documents over time with automatic version history.

**Version Features:**

- Automatic versioning on upload
- View previous versions
- Compare versions
- Restore old versions
    `,
        expandableSections: [
            {
                title: 'Viewing Version History',
                content: `
1. Select a document
2. Click **Version History** or right-click > Versions
3. See all versions with:
   - Version number
   - Upload date
   - Uploaded by
   - Size

Click a version to preview or download.
        `,
            },
            {
                title: 'Uploading New Versions',
                content: `
**Replace Document:**
1. Right-click the document
2. Select **Upload New Version**
3. Select the new file
4. The old version is preserved in history
5. The new version takes the old one's place in every folder it was filed in, with the same pin and position — you never have to re-file it

**Drag and Drop:**
- Drop a file with the same name
- System asks if you want to replace
- Choose "New Version" to preserve history
        `,
            },
        ],
        keywords: ['versions', 'history', 'revisions', 'previous versions'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.upload'],
    },
    {
        id: 'documents.comments',
        title: 'Comments and Markup',
        category: 'Documents',
        order: 5,
        summary: 'Adding comments and annotations to documents',
        content: `
Add comments and markup to documents for collaboration and review.

**Comment Features:**

- Text comments on documents
- Pin comments to specific locations
- Reply to comments
- Resolve comment threads
    `,
        expandableSections: [
            {
                title: 'Adding Comments',
                content: `
1. Open document in preview
2. Click the **Comments** button
3. Click on the document to place a pin
4. Type your comment
5. Click **Post**

Comments appear in the sidebar and as pins on the document.
        `,
            },
            {
                title: 'Managing Comments',
                content: `
**Reply:**
Click a comment and type a reply to continue the thread.

**Resolve:**
Mark a comment as resolved when the issue is addressed.

**Filter:**
View only unresolved comments, or all comments.
        `,
            },
        ],
        keywords: ['comments', 'annotations', 'markup', 'review', 'feedback'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.preview'],
    },
    {
        id: 'documents.workflows',
        title: 'Document Workflows',
        category: 'Documents',
        order: 6,
        summary: 'Managing document review and approval workflows',
        content: `
Workflows allow you to route documents through review and approval processes.

**Workflow Types:**

- Review workflows
- Approval workflows
- Custom workflows
    `,
        expandableSections: [
            {
                title: 'Starting a Workflow',
                content: `
1. Select document(s)
2. Click **Start Workflow**
3. Choose workflow template
4. Select participants
5. Set due dates
6. Add instructions
7. Click **Start**

Participants receive notifications of their tasks.
        `,
            },
            {
                title: 'Participating in Workflows',
                content: `
When assigned to a workflow:

1. Notification appears in your tasks
2. Open the document
3. Review/approve as required
4. Add comments if needed
5. Mark as complete

The workflow advances to the next step.
        `,
            },
        ],
        keywords: ['workflow', 'review', 'approval', 'routing'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.comments'],
    },
    {
        id: 'documents.attributes',
        title: 'Custom Attributes',
        category: 'Documents',
        order: 7,
        summary: 'Defining and using custom document attributes',
        content: `
Custom attributes allow you to add structured metadata to documents.

**Attribute Types:**

- Text
- Number
- Date
- Selection (dropdown)
- Yes/No
    `,
        expandableSections: [
            {
                title: 'Defining Attributes',
                content: `
1. Click **Attribute Definitions**
2. Click **Add Attribute**
3. Configure:
   - Name
   - Type
   - Options (for selection)
   - Required (yes/no)
4. Save

The attribute becomes available for all documents.
        `,
            },
            {
                title: 'Using Attributes',
                content: `
**Setting Values:**
1. Select a document
2. Open properties panel
3. Set attribute values
4. Save

**Filtering by Attributes:**
Use the filter row to filter documents by attribute values.
        `,
            },
        ],
        keywords: ['attributes', 'metadata', 'properties', 'custom fields'],
        route: '/documents',
        relatedArticles: ['documents.overview'],
    },
    {
        id: 'documents.splitter',
        title: 'Drawing Splitter',
        category: 'Documents',
        order: 8,
        summary: 'Splitting multi-page PDFs into individual drawings',
        content: `
The Drawing Splitter helps you split a multi-page PDF into individual drawing files.

**Use Cases:**

- Split combined drawing sets
- Extract specific pages
- Rename pages with proper drawing numbers
    `,
        expandableSections: [
            {
                title: 'Using the Splitter',
                content: `
1. Select a multi-page PDF
2. Click **Split Drawing**
3. Preview all pages
4. For each page:
   - Set drawing number
   - Set title
   - Choose to include/exclude
5. Click **Split**

Individual PDFs are created for each selected page.
        `,
            },
        ],
        keywords: ['split', 'extract', 'drawing splitter', 'PDF split'],
        route: '/documents',
        relatedArticles: ['documents.overview', 'documents.upload'],
    },
];
